import React, { useState, useEffect, useLayoutEffect, useMemo } from "react";
import _ from "lodash";
import { useFormikContext } from "formik";
import { loadWidgetsFromConfig } from "react-invenio-forms";
import { Container, Divider } from "semantic-ui-react";
import AccordionField from "./AccordionField";
import AddDiscoverableFieldsModal from "./AddDiscoverableFieldsModal";
import RepeatableField from "./RepeatableField";

const NON_REPEATABLE_FIELDS = ["Dropdown", "AutocompleteDropdown"];

const CustomFields = ({
  record,
  templateLoaders,
  includesPaths,
  fieldPathPrefix,
  config,
}) => {
  const [sections, setSections] = useState();
  const [newSection, setNewSection] = useState();
  const [discoverFieldsSections, setDiscoverFieldsSections] = useState();
  const [discoverableSectionsOrder, setDiscoverableSectionsOrder] = useState();

  const formikProps = useFormikContext();

  const filledRecordCustomFields = useMemo(() => {
    let filledFields = [];

    if (record && !_.isEmpty(record.custom_fields)) {
      filledFields = Object.keys(record.custom_fields).map(
        (key) => `custom_fields.${key}`
      );
    }

    return filledFields;
  }, [record]);

  useEffect(() => {
    populateConfig();
    const form = document.querySelector("#deposit-form form");
    if (form) {
      form.setAttribute("novalidate", "true");
    }
  }, []);

  useLayoutEffect(() => {
    if (newSection) {
      const element = document.getElementById(newSection);
      element.scrollIntoView({ behavior: "smooth" });
      setNewSection(null);
    }
  }, [newSection]);

  const hasValue = (section) =>
    section.fields.some((item) => filledRecordCustomFields.includes(item.key));

  const populateConfig = async () => {
    try {
      const { sectionsConfig, discoverFieldsConfig } =
        await loadCustomFieldsWidgets();

      const sections = sectionsConfig.map((sectionCfg) => {
        const paths = includesPaths(sectionCfg.fields, fieldPathPrefix);
        return { ...sectionCfg, paths };
      });

      const discoverFieldsSections = discoverFieldsConfig.map((sectionCfg) => {
        const paths = includesPaths(sectionCfg.fields, fieldPathPrefix);
        return { ...sectionCfg, paths };
      });

      const existingDiscoverFieldsSections = discoverFieldsSections?.filter(
        (section) => hasValue(section)
      );

      const displayableSections = [
        ...sections,
        ...existingDiscoverFieldsSections,
      ];

      const displayableDiscoverSections = _.xorBy(
        discoverFieldsSections,
        existingDiscoverFieldsSections,
        "section"
      );

      setSections(displayableSections);
      setDiscoverFieldsSections(displayableDiscoverSections);
      setDiscoverableSectionsOrder(
        discoverFieldsSections.reduce((result, current, index) => {
          result[current.section] = index;
          return result;
        }, {})
      );
    } catch (error) {
      console.error("Couldn't load custom fields widgets.", error);
    }
  };

  const handleAddSection = (newSection) => {
    setSections((prevState) => {
      const { simple, discoverable } = prevState.reduce(
        (result, current) => {
          if (current.discoverable_fields) {
            result.discoverable.push(current);
          } else {
            result.simple.push(current);
          }
          return result;
        },
        { simple: [], discoverable: [] }
      );

      discoverable.push(newSection);
      discoverable.sort(compareDiscoverableSections);
      return [...simple, ...discoverable];
    });
    setDiscoverFieldsSections((prevState) =>
      prevState.filter((item) => item.section !== newSection.section)
    );
    setNewSection(newSection.section);
  };

  const handleDeleteSection = (event, sectionTitle) => {
    event.preventDefault();
    event.stopPropagation();
    const index = sections.findIndex((item) => item.section === sectionTitle);

    const updatedSections = [...sections];
    const updatedDiscoverFieldsSections = [...discoverFieldsSections];

    if (index !== -1) {
      const [section] = updatedSections.splice(index, 1);

      section.fields.forEach((field) => {
        formikProps.setFieldValue(field.key, "");
      });

      updatedDiscoverFieldsSections.push(section);
      updatedDiscoverFieldsSections.sort(compareDiscoverableSections);
    }

    setSections(updatedSections);
    setDiscoverFieldsSections(updatedDiscoverFieldsSections);
  };

  const compareDiscoverableSections = (a, b) =>
    discoverableSectionsOrder[a.section] - discoverableSectionsOrder[b.section];

  const loadCustomFieldsWidgets = async () => {
    const sections = [];
    const discoverFieldsSections = []; // finds sections with discoverable fields
    for (const sectionCfg of config) {
      const loadedFields = [];
      for (const fieldCfg of sectionCfg.fields) {
        const path = `${fieldPathPrefix}.${fieldCfg.field}`;
        if (
          !NON_REPEATABLE_FIELDS.includes(fieldCfg.ui_widget) &&
          fieldCfg.props?.multiple
        ) {
          loadedFields.push(
            <RepeatableField
              key={path}
              fieldPath={path}
              fieldConfig={fieldCfg}
              templateLoaders={templateLoaders}
            />
          );
        } else {
          const widget = await loadWidgetsFromConfig({
            templateLoaders: templateLoaders,
            fieldPathPrefix: fieldPathPrefix,
            fields: [fieldCfg],
            record: record,
          });
          loadedFields.push(widget[0]);
        }
      }

      if (sectionCfg.discoverable_fields) {
        discoverFieldsSections.push({
          ...sectionCfg,
          fields: loadedFields,
          fieldsConfig: sectionCfg.fields,
        });
      } else {
        sections.push({ ...sectionCfg, fields: loadedFields });
      }
    }

    return {
      sectionsConfig: sections,
      discoverFieldsConfig: discoverFieldsSections,
    };
  };

  const groupFields = (fields) =>
    fields.reduce((result, current) => {
      const subgroup = current?.props.subgroup;
      const prevGroup = result[result.length - 1];
      if (subgroup && prevGroup?.title === subgroup) {
        prevGroup.fields.push(current);
      } else {
        result.push({ title: subgroup, fields: [current] });
      }
      return result;
    }, []);

  const renderFieldGroup = (group) => (
    <div>
      {group.title ? (
        <>
          <div>{group.title}</div>
          <Divider fitted className="rel-mb-1" />
          <div className="rel-ml-1">{group.fields}</div>
        </>
      ) : (
        <>{group.fields}</>
      )}
    </div>
  );

  return (
    <>
      {sections &&
        sections.map(
          ({
            fields,
            paths,
            displaySection = true,
            section: sectionName,
            discoverable_fields,
          }) => {
            const groupedFields = groupFields(fields);

            return displaySection ? (
              <AccordionField
                id={sectionName}
                key={`section-${sectionName}`}
                includesPaths={paths}
                label={sectionName}
                active
                deletable={discoverable_fields}
                onDeleteSection={handleDeleteSection}
              >
                {groupedFields.map((group) => renderFieldGroup(group))}
              </AccordionField>
            ) : (
              <Container key="custom-fields-section">{groupedFields}</Container>
            );
          }
        )}

      {discoverFieldsSections?.length > 0 && (
        <>
          <Divider fitted className="rel-mb-1" />
          <AddDiscoverableFieldsModal
            onSave={handleAddSection}
            sections={discoverFieldsSections}
          />
        </>
      )}
    </>
  );
};

CustomFields.defaultProps = {
  includesPaths: (fields) => fields.map((field) => field.key),
};

export default CustomFields;
